
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MegaphoneIcon, TrendingUp, DollarSign, Image, MessageSquare } from 'lucide-react';
import DoctorHeader from '@/components/doctor/DoctorHeader';
import DoctorFooterNav from '@/components/doctor/DoctorFooterNav';
import ActiveAdsOverview from '@/components/ads/ActiveAdsOverview';
import PerformanceStatistics from '@/components/ads/PerformanceStatistics';
import PurchasePromotion from '@/components/ads/PurchasePromotion';
import PaymentHistory from '@/components/ads/PaymentHistory';
import { useAdsDashboard } from '@/hooks/useAdsDashboard';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { Button } from '@/components/ui/button';

const DoctorAdsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { activeAds, adPerformance, paymentHistory, loading, error } = useAdsDashboard();

  const handleViewReceipt = async (adId: string) => {
    try {
      // In a real app, this would fetch and display the receipt
      toast({
        title: "Loading receipt",
        description: "Opening payment receipt..."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Could not load the receipt. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <DoctorHeader />
      
      <div className="container mx-auto p-4 pb-24 pt-20">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <MegaphoneIcon className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">My Advertisements</h1>
          </div>
          <Button onClick={() => navigate('/doctor-area/ads/manage')}>
            Manage All Promotions
          </Button>
        </div>
        
        <Tabs defaultValue="active-ads" className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="active-ads">
              <Image className="h-4 w-4 mr-2" />
              Active Ads
            </TabsTrigger>
            <TabsTrigger value="performance">
              <TrendingUp className="h-4 w-4 mr-2" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="purchase">
              <MegaphoneIcon className="h-4 w-4 mr-2" />
              Purchase Ads
            </TabsTrigger>
            <TabsTrigger value="payments">
              <DollarSign className="h-4 w-4 mr-2" />
              Payment History
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="active-ads" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Your Active Advertisements</CardTitle>
                <CardDescription>
                  Current promotional content visible to patients
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ActiveAdsOverview 
                  ads={activeAds} 
                  onViewDetails={(id) => navigate(`/doctor-area/ads/${id}`)}
                  onViewReceipt={handleViewReceipt}
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="performance" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Advertisement Performance</CardTitle>
                <CardDescription>
                  Analytics and insights about your promotional content
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PerformanceStatistics performance={adPerformance} />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="purchase" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Purchase New Promotions</CardTitle>
                <CardDescription>
                  Select from available promotional opportunities
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PurchasePromotion />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="payments" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Payment History</CardTitle>
                <CardDescription>
                  Your advertisement spending history
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PaymentHistory payments={paymentHistory} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      <DoctorFooterNav />
    </div>
  );
};

export default DoctorAdsPage;
